import { Directive, EventEmitter, Output, ViewChild, Input } from '@angular/core';
import {
  MatLegacyAutocomplete as MatAutocomplete,
  MatLegacyAutocompleteTrigger as MatAutocompleteTrigger,
} from '@angular/material/legacy-autocomplete';
import { Observable, Subject } from 'rxjs';
import { debounceTime, tap, takeUntil } from 'rxjs/operators';

@Directive({
  selector: 'mat-autocomplete[lumInfiniteScroll]',
  exportAs: 'mat-autocomplete[lumInfiniteScroll]',
})
export class InfiniteScrollDirective {
  @ViewChild(MatAutocompleteTrigger, { static: false })
  autoCompleteTrigger :MatAutocompleteTrigger | null = null;

  @Output() lumInfiniteScroll = new EventEmitter<void>();

  @Input() infiniteScrollFetchingData = false;
  @Input() infiniteScrollThreshold = 0.8; // 80% from bottom
  @Input() infiniteScrollDebounce = 200; // ms

  private onDestroy$ = new Subject<void>();
  private scrollSubject = new Subject<Event>();
  private readonly onScrollBound :(event :Event) => void;

  constructor(public autoComplete :MatAutocomplete) {
    this.onScrollBound = this.onScroll.bind(this);

    this.scrollSubject
      .pipe(debounceTime(this.infiniteScrollDebounce), takeUntil(this.onDestroy$))
      .subscribe((event) => this.handleScroll(event));
  }

  ngOnInit() :void {
    this.onAutoCompleteOpen().subscribe();
    this.onAutoCompleteClose().subscribe();
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.removeScrollEventListener();
  }

  private onScroll(event :Event) {
    this.scrollSubject.next(event);
  }

  private handleScroll(event :Event) {
    const element = event.target as HTMLElement;
    const { scrollTop, scrollHeight, clientHeight } = element;
    const scrollPosition = (scrollTop + clientHeight) / scrollHeight;

    if (
      scrollPosition > this.infiniteScrollThreshold &&
      scrollTop > 0 &&
      !this.infiniteScrollFetchingData
    ) {
      this.lumInfiniteScroll.emit();
    }
  }

  private onAutoCompleteOpen() :Observable<void> {
    return this.autoComplete.opened.pipe(
      tap(() => {
        setTimeout(() => {
          this.removeScrollEventListener();
          this.autoComplete.panel.nativeElement.addEventListener(
            'scroll',
            this.onScrollBound,
          );
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private onAutoCompleteClose() :Observable<void> {
    return this.autoComplete.closed.pipe(
      tap(() => this.removeScrollEventListener()),
      takeUntil(this.onDestroy$),
    );
  }

  private removeScrollEventListener() :void {
    if (this.autoComplete?.panel) {
      this.autoComplete.panel.nativeElement.removeEventListener(
        'scroll',
        this.onScrollBound,
      );
    }
  }
}
